import _, { first } from "lodash";
import React, { MutableRefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Params } from "react-router-dom";
import { Socket } from "socket.io-client";
import { gameSituation, initialSetup, sourcePlayer } from "../../implementationGlobals";
import {
  getComponentsImport,
  getCustomMovesImport,
  getCustomTooltipImport,
  getHighlightImport,
  getMoveManagerImport,
  getStatesImport,
} from "../projectImports";
import { useImmer } from "use-immer";
import { getAdjustFunction } from "../gameMoveEvaluation";
import { manipulationMethods, stateOfGameManager } from "../StateOfGameManipulation";
import {
  addChildData,
  adjustComponentPool,
  audio,
  getAllComponentChildren,
  getAllComponentDirectChildren,
  importAll,
  isWebkit,
  logMessage,
  mergeDeep,
  processComponentInfo,
} from "../tableUtility";
import { createEventManager } from "./eventManager";
import {
  GameComponentForGame,
  EvalClick,
  GeneralComponentForGame,
  PossiblyUnknownGameComponent,
  GameState,
  IsPosVisible,
  GameMoveData,
  EvalGameEvent,
  EvalCustomMove,
  AddStateButtons,
  EventManagerForGame,
  GameStateButton,
  SystemLogMessage,
  PlayerInfo,
  GameMoveForEval,
  RevealComponentData,
  SetCounterData,
} from "src/components/implementationTypes";
import {
  PlayerEvent,
  EventManager,
  GameSituationBackend,
  ComponentDataSource,
  PlayerColorInfo,
  TableInfo,
  DeckInfo,
  LogMessageData,
  MessageData,
  AllStats,
  GameComponent,
  GameComponentAnimationData,
  SetupTableData,
  ReloadEvent,
  DataSheet,
  ComponentPool,
  GridPositionProps,
} from "src/components/types";
import { TableContext, TableContextType } from "../tableContext";
import { loadFonts } from "src/components/CardGenerator";
import { start } from "repl";

window.mobileCheck = function () {
  let check = false;
  // @ts-ignore
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

export type GameTableLogic = {
  moveToEvent: (eventNumber: number) => void;
  allEventsRef: MutableRefObject<PlayerEvent[]>;
  currEventNumber: number;
  tableStatus: string;
  eventManager: EventManager;
  isClickable: (componentData: GameComponentForGame, relativeState?: GameSituationBackend) => string | boolean;
  evalClick: EvalClick;
  customTooltip: (componentData: GeneralComponentForGame) => string;
  tableStateRef: MutableRefObject<GameSituationBackend | undefined>;
  workingTableStateRef: MutableRefObject<GameSituationBackend | undefined>;
  getPcCoords: any; //TODO improve
  getComponentInfo: (componentData: PossiblyUnknownGameComponent) => ComponentDataSource | undefined;
  adjustGameState: (state: GameState) => GameState;
  playerColors: PlayerColorInfo[] | undefined;
  tableData: TableInfo | undefined;
  selectedColor: string | undefined;
  playAsColor: string;
  makeTurnsAs?: string;
  decks: { [key: string]: DeckInfo } | undefined;
  switchPlayer: (color: string) => void;
  evalAutoStates: (
    gameSituation: GameSituationBackend,
    eventManager: EventManager,
    sourcePlayer?: string | undefined,
  ) => void;
  reloadComponents: () => void;
  setEditMode: (newValue: boolean) => void;
  getImage: (imageName: string, cssPath?: boolean | undefined) => string | undefined;
  isPosVisible: IsPosVisible;
  getTargetScale: () => number;
  editMode: boolean;
  windowWidth: number;
  clipLog: any; //TODO
  skipAnimation: any; //TODO
  cheatMode: boolean;
  defaultSelected: string | undefined;
  statusBarStatus: string;
  outOfSync: boolean;
  tableLoaded: boolean;
  changeCurrentPlayer: (newColor: string) => void;
  dialogMessages: string[];
  dialogType: string;
  processDialogConfirmed: () => void;
  processDialogRejected: () => void;
  processAlertCheated: () => void;
  setCheatMode: (newValue: boolean) => void;
  logMessages: LogMessageData[];
  fetchTable: () => void;
  selectedComponent: GameComponentForGame | null;
  setUserSelectedComponent: (componentData: GameComponentForGame | null) => void;
  connectedPlayers: Set<string>;
  globalAssets: { [key: string]: any } | undefined;
  tableId: string;
};

export type GameTableLogicInput = {
  params: Readonly<Params<string>>;
  query: URLSearchParams;
  socket: Socket;
  scaleAdjust: number;
  evalSpeed: number;
  setDeckReloadStatus: (newValue: string) => void;
  setDataSheetReloadStatus: (newValue: string) => void;
  playAsColor: string;
  setPlayAsColor: (newValue: string) => void;
  setCanSelectColor: (newValue: boolean) => void;
  setChatMessages: (value: React.SetStateAction<MessageData[]>) => void;
  setPopupMessage: (value: React.SetStateAction<MessageData | undefined>) => void;
  setAllStats: (value: React.SetStateAction<AllStats>) => void;
  setAlert: (newMessage: string, newColor: string) => void;
  setTableJoinFailedDialogReason: (newValue: string | null) => void;
};

// Hex directions constant: [E, NE, NW, W, SW, SE] — hoisted to module level
const HEX_DIRECTIONS: [number, number][] = [
  [1, 0],
  [0.5, Math.sqrt(3) / 2],
  [-0.5, Math.sqrt(3) / 2],
  [-1, 0],
  [-0.5, -Math.sqrt(3) / 2],
  [0.5, -Math.sqrt(3) / 2],
];
const SQRT3_OVER_4 = Math.sqrt(3) / 4;
const PILE_XD_TABLE = [0, 0, -0.5, -0.5, -0.25, 0, 0, 0, -0.25, -0.5, -0.5, -0.5, -0.5, -0.5, -0.25, 0];
const PILE_XY_TABLE = [
  0,
  0,
  0,
  -SQRT3_OVER_4,
  -SQRT3_OVER_4,
  -SQRT3_OVER_4,
  0,
  0,
  0,
  0,
  0,
  -SQRT3_OVER_4,
  -SQRT3_OVER_4,
  -SQRT3_OVER_4,
  -SQRT3_OVER_4,
  -SQRT3_OVER_4,
  -SQRT3_OVER_4,
];

export function useGameTableLogic({
  params,
  query,
  socket,
  scaleAdjust,
  evalSpeed,
  setDeckReloadStatus,
  setDataSheetReloadStatus,
  playAsColor,
  setPlayAsColor,
  setCanSelectColor,
  setChatMessages,
  setPopupMessage,
  setAllStats,
  setAlert,
  setTableJoinFailedDialogReason,
}: GameTableLogicInput): GameTableLogic {
  const tableStateRef = useRef<GameSituationBackend | undefined>(undefined);
  const workingTableStateRef = useRef<GameSituationBackend | undefined>(undefined);
  const startPositionRef = useRef<GameSituationBackend | null>(null);
  const allEventsRef = useRef<PlayerEvent[]>([]);
  const imagesRef = useRef<{ [key: string]: string | { default: string } } | null>(null);
  const mutedRef = useRef(false);

  const [tableStatus, setTableStatus] = useState<string>("");
  //const [currEventNumber, setCurrEventNumber] = useState(0);
  const currEventNumber = useRef(0);
  const [logMessages, setLogMessages] = useState<LogMessageData[]>([]);

  let preselectedColor = query.get("playAsColor") ?? undefined; // cast null to undefined
  if (
    tableStateRef.current &&
    !tableStateRef.current?.options?.selectedColors?.concat(["spectator", "alwaysactive"]).includes(preselectedColor)
  ) {
    preselectedColor = tableStateRef.current?.players?.[0]?.color;
  }
  const [selectedColor, setSelectedColor] = useState(preselectedColor);

  const [editMode, setEditMode] = useState<boolean>(false);
  const [cheatMode, setCheatMode] = useState<boolean>(false);
  const [confirmMessages, setConfirmMessages] = useState<string[]>([]);
  const [confirmPopupResolve, setConfirmPopupResolve] = useState<(() => void) | undefined>();
  const [confirmPopupMessage, setConfirmPopupMessage] = useState<string | undefined>();
  const [confirmMovesToSend, setConfirmMovesToSend] = useState<GameMoveData[]>([]);
  const [statusBarStatus, setStatusBarStatus] = useState("waitUserInput");
  const [outOfSync, setOutOfSync] = useState<boolean>(false);
  const turnToConfirm = useRef(-1);
  // bumped when the server confirms our turn: turnToConfirm is a ref, so clearing it alone doesn't re-render
  const [confirmedTurnCount, setConfirmedTurnCount] = useState(0);
  const [defaultSelected, setDefaultSelected] = useState<string | undefined>();
  const [firstLoadDelayedChecked, setFirstLoadDelayedChecked] = useState(false);

  const [generalIsClickable, setGeneralIsClickable] = useState<
    (componentData: GameComponentForGame, gameSituation: GameSituationBackend) => boolean | string
  >(() => false);
  const [customTooltip, setCustomTooltip] = useState<(componentData: GeneralComponentForGame) => string>(
    (componentData: GeneralComponentForGame) => "",
  );
  const [tableData, setTableData] = useImmer<TableInfo | undefined>(undefined);
  const [decks, setDecks] = useState<{ [key: string]: DeckInfo }>();
  const [globalAssets, setGlobalAssets] = useState<{ [key: string]: any }>();

  const [isEditorBound, setIsEditorBound] = useState<boolean>(false);

  const [playerColors, setPlayerColors] = useState<PlayerColorInfo[]>([]);
  const [componentPool, setComponentPool] = useState({});
  const [tableLoaded, setTableLoaded] = useState(false);
  const [windowWidth, setWindowWidth] = useState<number>(0);

  const getComponentInfoRef = useRef<(componentData: GeneralComponentForGame) => ComponentDataSource | undefined>(
    () => undefined,
  );
  const componentInfoCacheRef = useRef<{
    tableStateVersion: GameSituationBackend | undefined;
    componentPoolVersion: any;
    adjustedCurrentState: GameState | null;
    results: Map<GeneralComponentForGame | string, ComponentDataSource | undefined>;
  }>({
    tableStateVersion: undefined,
    componentPoolVersion: undefined,
    adjustedCurrentState: null,
    results: new Map(),
  });
  const isClickableCacheRef = useRef<{
    relativeStateVersion: GameSituationBackend | undefined;
    activeStateVersion: GameState | undefined;
    adjustedState: GameState | null;
    setupDone: boolean;
  }>({
    relativeStateVersion: undefined,
    activeStateVersion: undefined,
    adjustedState: null,
    setupDone: false,
  });
  const evalGameEventRef = useRef<EvalGameEvent>(() => false);
  const tableFirstVisitRef = useRef<(() => boolean | void) | undefined>(undefined);
  const evalCustomMoveRef = useRef<EvalCustomMove>(() => false);
  const isPosVisibleRef = useRef<any>();
  const evalClickRef = useRef<any>();
  const addStateButtonsRef = useRef<AddStateButtons | undefined>();
  const makeTurnsAs = useMemo<string | undefined>(() => {
    if (playAsColor === "alwaysactive") {
      let activeState = tableStateRef.current?.getActiveState();
      if (playerColors?.map((c) => c.color)?.includes(activeState?.activePlayer ?? "")) {
        return activeState?.activePlayer;
      }
      return selectedColor ?? playAsColor;
    }
    return playAsColor;
  }, [playAsColor, selectedColor]);

  const statesInfo = useRef<any>();
  const [userSelectedComponent, setUserSelectedComponent] = useState<GameComponentForGame | null>(null);
  const [connectedPlayers, setConnectedPlayers] = useState<Set<string>>(new Set());
  const selectedComponent = useMemo(() => {
    if (tableStateRef.current === undefined) {
      return null;
    }
    initialSetup(workingTableStateRef.current as GameSituationBackend);
    let stateInfo = adjustGameState(tableStateRef.current?.getActiveState() ?? {}, [
      "forceSelectedComponent",
      "defaultSelected",
    ]);
    let forceSelectedComponent = stateInfo?.forceSelectedComponent;
    if (forceSelectedComponent) {
      if (typeof forceSelectedComponent === "function") {
        forceSelectedComponent = forceSelectedComponent(tableStateRef.current?.getActiveState(), {});
      }
    }
    return (
      tableStateRef.current?.getGameComponentById(forceSelectedComponent) ??
      userSelectedComponent ??
      tableStateRef.current?.getGameComponentById(defaultSelected) ??
      null
    );
  }, [
    tableStateRef.current,
    userSelectedComponent,
    tableStateRef.current?.getActiveState().forceSelectedComponent,
    getViewingAsColor(),
  ]);
  let legitEval = true;
  const evalNextTableEvent = useCallback(
    async (isPremove: boolean = false, confirmed = false) => {
      if (statusBarStatus === "waitingEvaluate") {
        return; // prevents multiple events being evaled at same time, will be checked after current event finishes evaling
      }
      let msg = window.tableEventQueue[0];
      let stopped = false;
      async function evalNextEvent() {
        if (!msg) {
          return;
        }

        switch (msg.type) {
          case "chat-message":
            setChatMessages((chatMessages) => [...chatMessages, msg as MessageData]);
            setPopupMessage(msg as MessageData);
            audio.chatPop.play();
            break;
          case "player-joined":
            let msgData = {
              type: "player-joined",
              timestamp: Date.now(),
              username: (msg as MessageData).username,
              message: (msg as MessageData).username + " has joined the chat",
            };
            setChatMessages((chatMessages) => [...chatMessages, msgData]);
            setConnectedPlayers(
              (players) => new Set([...players, (msg as MessageData).username].filter((a) => a !== undefined)),
            );
            setPopupMessage(msgData as MessageData);
            audio.chatPop.play();
            break;
          case "player-disconnected": {
            setConnectedPlayers((players) => new Set([...players].filter((a) => a !== (msg as MessageData).username)));
            let msgData = {
              type: "player-disconnected",
              timestamp: Date.now(),
              username: (msg as MessageData).username,
              message: (msg as MessageData).username + " has left the chat",
            };
            setChatMessages((chatMessages) => [...chatMessages, msgData]);
            break;
          }
          case "reload-data-sheet":
            if (tableStateRef.current) {
              tableStateRef.current.dataSheet = (msg as ReloadEvent).dataSheet as DataSheet;
            }
            setDataSheetReloadStatus("New data sheet loaded");
            break;
          case "reload-dev-editor-components": {
            let newComponents = (msg as ReloadEvent).componentPool as ComponentPool;
            window.componentPool = newComponents;
            setComponentPool(newComponents);

            break;
          }
          case "reload-decks": {
            setDeckReloadStatus("New decks loaded");
            setDecks((msg as ReloadEvent).decks);
            break;
          }

          case "game-move": {
            let workingMsg = msg as PlayerEvent;
            if (workingMsg?.data?.turnUID && !workingMsg.data.turnNonBlocking) {
              if (window.evaluatedMoves?.has(workingMsg.data.turnUID)) {
                legitEval = false;
                return;
              }
              window.evaluatedMoves.add(workingMsg.data.turnUID);
            }
            if (outOfSync) {
              return;
            }
            setStatusBarStatus("waitUserInput");
            let newEvents = JSON.parse(JSON.stringify(allEventsRef.current)).concat(
              JSON.parse(JSON.stringify([msg])),
            ) as PlayerEvent[];
            allEventsRef.current = newEvents;
            console.log("new table event received", msg);

            let resultData = workingMsg.data;
            if (resultData?.moveList) {
              let stateDraft = Object.assign(
                _.cloneDeep(stateOfGameManager),
                JSON.parse(JSON.stringify(tableStateRef.current || startPositionRef.current)),
              );
              let turnMessage = logMessage(
                {
                  logMessage: "Turn {currTurn}",
                  currTurn: resultData.moveId,
                  systemTurn: true,
                  timestamp: Date.now(),
                  logStyle: {
                    fontSize: "8px",
                    outline: "none",
                    margin: "-5px 0",
                  },
                } as SystemLogMessage,
                undefined,
                resultData.turnHidden,
              );

              let newMessages = [turnMessage];

              setStatusBarStatus("waitingEvaluate");
              let allEventsDone = false;
              window.skipAnimation = false;
              for (let move of resultData.moveList) {
                move.username = workingMsg.username;
                move.timestamp = workingMsg.timestamp;
                if (move.gameMoveUID && stateDraft?.evaledMoves) {
                  if (stateDraft.evaledMoves[move.gameMoveUID]) {
                    continue;
                  }
                  stateDraft.evaledMoves[move.gameMoveUID] = true;
                }
                let delay = move.moveDelay;
                let moveTimeout = move.moveTimeout;
                if (move.type === "inc counter" && move.sourceComponent && +(move.amt ?? 0) !== 0) {
                  if (delay === undefined) {
                    delay = 100;
                  }
                  if (moveTimeout === undefined) {
                    moveTimeout = 500;
                  }
                }
                if (move.type === "component move" && moveTimeout === undefined) {
                  moveTimeout = 500;
                }
                if (move.type === "set global" && moveTimeout === undefined) {
                  moveTimeout = 600;
                }
                let isStateChange = ["set gamestate", "add substate", "resolve curr state"].includes(move.type ?? "");
                if (isStateChange && moveTimeout === undefined) {
                  moveTimeout = 0;
                }
                if (isStateChange && delay === undefined) {
                  delay = 0;
                }
                if (window.skipAnimation) {
                  delay = moveTimeout = 0;
                }
                if (delay !== undefined) {
                  delay /= evalSpeed;
                }
                if (moveTimeout !== undefined) {
                  moveTimeout /= evalSpeed;
                }
                if (delay && !move.skipAnimation) {
                  await new Promise((r) => setTimeout(r, delay));
                }

                if (move.popupMessage && !confirmed) {
                  setConfirmPopupMessage(move.popupMessage);
                  await new Promise<void>((r) => {
                    setConfirmPopupResolve(() => r);
                  });
                  setConfirmPopupMessage(() => undefined);
                }
                evalGameMove(move, stateDraft, newMessages, true);
                tableStateRef.current = stateDraft;

                allEventsDone = false;
                if (moveTimeout) {
                  allEventsDone = true;
                  tableStateRef.current = stateDraft;

                  let _newMessages = [...newMessages]; // idk why we need copy, but eslint complains about unsafe reference otherwise
                  setLogMessages((m) => m.concat(_newMessages));
                  newMessages = [];
                  if (!move.skipAnimation) {
                    await new Promise((r) => setTimeout(r, moveTimeout));
                  }
                }
              }
              if (!allEventsDone) {
                tableStateRef.current = stateDraft;
                setLogMessages((m) => m.concat(newMessages));
              }

              setStatusBarStatus("waitUserInput");
              if (tableStateRef.current?.lastActivePlayer) {
                activatePlayer(tableStateRef.current.lastActivePlayer);
              }
            } else if (workingMsg.data) {
              workingMsg.data.username = workingMsg.username;
              workingMsg.data.timestamp = workingMsg.timestamp;
              evalGameMove(workingMsg.data as unknown as GameMoveData);
            }
            break;
          }
          case "undo": {
            let newEvents = JSON.parse(JSON.stringify(allEventsRef.current)).concat(JSON.parse(JSON.stringify([msg])));
            allEventsRef.current = newEvents;
            let eventIndex = newEvents.length - 1;
            let workingMsg = msg as PlayerEvent;
            let undoAmt = workingMsg.data?.undoAmt || 1;
            while (undoAmt > 0) {
              while (newEvents[eventIndex].__undone || newEvents[eventIndex].type == "undo") {
                --eventIndex;
                //--undoAmt;
                if (eventIndex < 0) {
                  break;
                }
              }
              if (eventIndex >= 0) {
                newEvents[eventIndex].__undone = true;
              }
              --undoAmt;
            }
            allEventsRef.current = newEvents;

            let startCopy = getStartPositionCopy();
            tableStateRef.current = startCopy;

            let allEvents = JSON.parse(JSON.stringify(allEventsRef.current));
            mutedRef.current = true;
            evalEvents(allEvents);
            workingTableStateRef.current = _.cloneDeep(tableStateRef.current);
            mutedRef.current = false;
            setStatusBarStatus("waitUserInput");
            break;
          }
          default:
            console.warn("Unknown event type " + msg.type);
        }
      }
      let prevActive = tableStateRef.current?.getActiveState().activePlayer;
      await evalNextEvent();
      let newActive = tableStateRef.current?.getActiveState().activePlayer;
      if (
        prevActive !== newActive &&
        newActive === playAsColor &&
        (tableStateRef.current?.players?.filter((p) => !p.isAutoma).length ?? 2) > 1
      ) {
        audio.ding.volume = 0.5;
        audio.ding.play();
      }
      window.tableEventQueue.splice(window.tableEventQueue.indexOf(msg), 1);
      if (window.tableEventQueue.length) {
        await evalNextTableEvent();
      } else if (
        msg.type === "game-move" &&
        allEventsRef.current.filter((e) => !e.data?.turnHidden && !e.data?.turnNonBlocking).slice(-1)[0]?.data
          ?.moveId === ((msg as PlayerEvent)?.data?.moveId ?? 0) &&
        !isPremove
      ) {
        let gMsg = msg as PlayerEvent;
      }

      if (
        window.tableEventQueue.length === 0 &&
        (msg as PlayerEvent)?.data?.moveList.find((e) => e.type === "game end")
      ) {
        let allEvents = JSON.parse(JSON.stringify(allEventsRef.current)) as GameMoveData[];
        let notUndoneEvents = allEvents.filter((e) => !e.__undone && e.type !== "undo");
        allEventsRef.current = notUndoneEvents;
        currEventNumber.current = allEventsRef.current.length;
        generateFullLog(allEventsRef.current);
      }
    },
    [
      tableStateRef,
      allEventsRef,
      mutedRef,
      activatePlayer,
      audio,
      evalEvents,
      getStartPositionCopy,
      logMessages,
      socket,
      stateOfGameManager,
      evalGameMove,
      adjustGameState,
      tableStatus,
      statusBarStatus,
      turnToConfirm,
    ],
  );

  const getImage = useCallback(
    (imageName: string, cssPath: boolean = true) => {
      if (!imageName) {
        return;
      }
      let urlBase = "";
      if (localStorage.getItem("server-env") === "dev") {
        if (imagesRef.current && imagesRef.current[imageName]) {
          let imageValue: string | { default: string } = imagesRef.current[imageName];
          if (typeof imageValue == "string") {
            urlBase = imageValue;
          } else {
            urlBase = imageValue?.default;
          }
        }
      } else {
        urlBase = "https://storage.googleapis.com/cgo-projects/" + params.projectSlug + "/assets/" + imageName;
      }
      if (cssPath) {
        return "url(" + urlBase + ")";
      }
      return urlBase;
    },
    [imagesRef, params.projectSlug],
  );
  const eventManager = createEventManager(
    socket,
    params.tableId ?? "",
    editMode,
    cheatMode,
    setCheatMode,
    setConfirmMovesToSend,
    allEventsRef,
    tableStateRef,
    evalGameMove,
    getImage,
    createConfirm,
    setStatusBarStatus,
    turnToConfirm,
    newEventCallBack,
    setAlert,
    evalNextTableEvent,
  );

  function evalAutoStates(gameSituation: GameSituationBackend, eventManager: EventManager, sourcePlayer?: string) {
    // we use stringify to compare state to detect changes in one state
    let lastLen = 0;
    initialSetup(gameSituation, eventManager as EventManagerForGame, sourcePlayer);
    for (;;) {
      autoEvalState();

      if (eventManager.gameMoveList.length === lastLen) {
        break;
      }
      if (gameSituation.ended) {
        break;
      }
      if (lastLen >= 1000) {
        throw new Error("Maximum number of game moves exceeded - possible infinite loop");
      }
      lastLen = eventManager.gameMoveList.length;
    }
    function autoEvalState() {
      let oldState = gameSituation.getActiveState();
      let stateInfo = adjustGameState(oldState);
      if (stateInfo.__markResolved) {
        eventManager.addGameMove(
          {
            type: "resolve curr state",
          },
          gameSituation as GameSituationBackend,
        );
        return;
      }
      if (typeof stateInfo.autoEvalState === "function") {
        stateInfo.autoEvalState(gameSituation, eventManager, sourcePlayer);
      }
    }
    let finalState = gameSituation.getActiveState();
    let stateInfo = adjustGameState(finalState);

    setDefaultSelected(undefined);
    if (typeof stateInfo.defaultSelected === "function") {
      setDefaultSelected(stateInfo.defaultSelected());
    }
  }

  function getViewingAsColor() {
    if (["alwaysactive", "spectator"].includes(playAsColor)) {
      return selectedColor;
    }
    return playAsColor;
  }

  function adjustGameState(state: GameState, restrictTo: string[] | undefined = undefined) {
    let newState = JSON.parse(JSON.stringify(state));
    if (!statesInfo.current || !statesInfo.current[state?.name ?? ""]) {
      return newState;
    }
    let newStateInfo = statesInfo.current[newState.name];
    if (newStateInfo) {
      for (let attr in newStateInfo) {
        if (!newState[attr] && (!restrictTo || restrictTo.includes(attr))) {
          let newValue = newStateInfo[attr];
          if (typeof newValue === "function") {
            newValue = newValue(newState, { viewingAsColor: getViewingAsColor() });
          }
          newState[attr] = newValue;
        }
      }
    }
    if (addStateButtonsRef.current) {
      if (!newState.buttons) {
        newState.buttons = [];
      }
      initialSetup(workingTableStateRef.current as GameSituationBackend);
      newState.buttons = (newState.buttons ?? []).concat(addStateButtonsRef.current());
    }
    return newState;
  }

  function preEvalGameEvent(gameSituation: GameSituationBackend, moveData: GameMoveData, eventManager: EventManager) {
    initialSetup(gameSituation, eventManager as EventManagerForGame, moveData.sourcePlayer);
    let state = adjustGameState(gameSituation.getActiveState()) as GameState;
    let evalResult;
    workingTableStateRef.current = gameSituation;
    let prevActive = state.activePlayer;

    let stateButtons = state.buttons;
    if (moveData.editMode) {
      stateButtons = stateButtons?.concat(state.editModeButtons ?? []);
    }
    let stateButtonResult = false;
    if (stateButtons && moveData.buttonName) {
      let targetButton = stateButtons.find((b: GameStateButton) => b.name === moveData.buttonName);
      if (typeof targetButton?.onClick === "function") {
        if (targetButton.onClick.length === 0) {
          stateButtonResult = targetButton.onClick();
        } else {
          stateButtonResult = targetButton.onClick(
            gameSituation,
            eventManager as EventManagerForGame,
            moveData.sourcePlayer ?? "",
          );
        }
      }
    }
    if (moveData.delayedEval && !state.__markResolved) {
      if (typeof state.delayedEval === "function") {
        evalResult = state.delayedEval(gameSituation, eventManager, moveData.sourcePlayer);
      }
      if (!tableFirstVisitRef.current) {
        evalResult = true;
      } else if (moveData.type === "table-first-visit") {
        eventManager.preventUndo("You cannot undo game setup");
        evalResult = tableFirstVisitRef.current();
      }
    }
    if (stateButtonResult) {
      evalAutoStates(gameSituation, eventManager, moveData.sourcePlayer);
      return stateButtonResult;
    }
    if (typeof state.behavior?.evalGameEvent === "function") {
      if (state.behavior.evalGameEvent.length === 1) {
        evalResult = state.behavior.evalGameEvent(moveData);
      } else {
        evalResult = state.behavior.evalGameEvent(gameSituation, moveData, eventManager);
      }
    }
    if (evalResult) {
      evalAutoStates(gameSituation, eventManager, moveData.sourcePlayer);
      return evalResult;
    }
    let eventResult;
    eventResult = evalGameEventRef.current(gameSituation, moveData, eventManager);
    if (!eventResult) {
      eventManager.addGameMove(moveData, gameSituation);
    }
    evalAutoStates(gameSituation, eventManager, moveData.sourcePlayer);
  }

  function newEventCallBack(
    eventName: string,
    options: PlayerEvent,
    thisArg: EventManager,
    hideError: boolean = false,
  ) {
    options.editMode = editMode;
    if (eventName === "delayed eval") {
      options.delayedEval = true;
      options.sourcePlayer = playAsColor ?? "";
      if (playAsColor === "alwaysactive") {
        options.sourcePlayer = tableStateRef.current?.getActiveState().activePlayer;
      }
    }
    thisArg.revealTempId = 0;
    thisArg._preventUndo = false;

    let visibleEvents = allEventsRef.current.filter(
      (e) => !e.data?.turnHidden && !e.data?.turnNonBlocking,
    ) as PlayerEvent[];
    if (eventName === "undo") {
      let cleanEvents = allEventsRef.current.filter((e) => e.type !== "undo" && !e.__undone) as PlayerEvent[];
      let previousMoveId = allEventsRef.current
        .filter((e) => !e.data?.turnHidden && !e.__undone)
        .map((e) => e.data?.moveId)
        .at(-1);
      let moveId = previousMoveId !== undefined ? previousMoveId + 1 : 0;
      options.moveId = moveId;
      if (editMode) {
        options.undoAmt = 1;
      } else {
        if (
          options.sourcePlayer !== tableStateRef.current?.getActiveState().activePlayer &&
          tableStateRef.current?.getActiveState().activePlayer !== "automa"
        ) {
          thisArg.createAlert("Cannot undo someone else's turn", "red");
          return;
        }

        let undoAmt = 0;
        let eventIndex = cleanEvents.length - 1;
        let currEvent = cleanEvents[eventIndex];
        let lastEvent = cleanEvents[cleanEvents.length - 1];
        if (lastEvent?.data?.preventUndo) {
          let message = "You cannot undo this turn";
          if (typeof lastEvent?.data?.preventUndo === "string") {
            message = "You cannot undo this turn: " + lastEvent?.data?.preventUndo;
          }
          thisArg.createAlert(message, "red", true);

          return;
        }
        while (!currEvent?.data?.undoBreakpoint) {
          if (!currEvent) {
            break;
          }
          while (currEvent.type === "undo" || currEvent.__undone) {
            --eventIndex;
            if (eventIndex <= 0) {
              break;
            }
            currEvent = cleanEvents[eventIndex];
          }

          ++undoAmt;
          --eventIndex;
          currEvent = cleanEvents[eventIndex];
          if (undoAmt > 50) {
            undoAmt = 1;
            break;
          }
        }
        if (!currEvent?.data?.preventUndo) {
          undoAmt += 1;
        }
        options.undoAmt = undoAmt;
      }
    }

    if (eventName === "game move" || eventName === "delayed eval" || eventName === "eval auto states") {
      thisArg.gameMoveList = [];
      thisArg.movePreventReasons = [];
      thisArg.confirmMessages = [];
      thisArg.replayMarks = [];
      workingTableStateRef.current = _.cloneDeep(tableStateRef.current);
      let workingStateTemp = workingTableStateRef.current;
      thisArg.oldActivePlayer = workingStateTemp?.getActiveState().activePlayer;
      if (tableStatus !== "midgame" && options.type !== "table-first-visit" && options.type !== "delayed-eval") {
        thisArg.preventMove("You can only do moves in midgame tables");
      }
      if (editMode && eventName !== "delayed eval") {
        thisArg.incStat("__editmode-moves", 1, workingStateTemp as GameSituationBackend);
        thisArg.incStat("__editmode-moves", 1, workingStateTemp as GameSituationBackend, options.sourcePlayer);
      }

      try {
        if (!editMode && options.onlyEditMode) {
          throw new Error("This button can only be used in edit mode");
        }
        let evalAutomation =
          (!editMode && !options.onlyEditMode) ||
          (editMode && options.onlyEditMode) ||
          (editMode && options.editModeButton) ||
          (editMode && eventName === "delayed eval");
        if (evalAutomation) {
          preEvalGameEvent(workingStateTemp as GameSituationBackend, options, thisArg);
        }
        if (editMode && eventName !== "delayed eval") {
          thisArg.addGameMove(options, workingStateTemp as GameSituationBackend);
        }
      } catch (errMessage: any) {
        if (hideError) {
          return;
        }
        if (thisArg.showMovePrevent(hideError)) {
          return;
        }
        thisArg.createAlert(("System error: " + errMessage?.message) as string, "blue");
        console.error(errMessage);
        return;
      }
      if (!cheatMode && thisArg.showMovePrevent(hideError)) {
        thisArg.sendMoves(thisArg.gameMoveList.filter((e) => e.forceEvaluate));
        return;
      }
      let activePlayer = thisArg.oldActivePlayer;
      let isAutomaActive = workingStateTemp?.players.find((p) => p.color === activePlayer)?.isAutoma;
      if (
        typeof activePlayer === "string" &&
        activePlayer !== sourcePlayer &&
        !editMode &&
        activePlayer !== "everyone" &&
        options.type !== "delayed-eval" &&
        options.type !== "table-first-visit" &&
        !isAutomaActive
      ) {
        thisArg.showMovePrevent("You cannot play now: Wait for your turn");
        return;
      }
      if (thisArg.confirmMessages.length) {
        thisArg.sendMoves(thisArg.gameMoveList.filter((e) => e.forceEvaluate));
        createConfirm(thisArg.confirmMessages);
      } else if (
        thisArg.sendMoves &&
        (thisArg.gameMoveList.filter((m) => m.logMessage || m.type !== "delayed eval").length ||
          options.type === "table-first-visit")
      ) {
        if (options.type === "table-first-visit") {
          if (thisArg.gameMoveList.length === 0) {
            return;
          }
          thisArg.addGameMove({ type: "table first visit" });
        }
        thisArg.sendMoves();
        setCheatMode(false);
      }
    } else {
      thisArg.socket?.emit(
        eventName,
        JSON.stringify(
          Object.assign({
            tableId: thisArg.tableId,
            username: localStorage.getItem("user-name"),
            type: eventName,
            data: options,
          }),
        ),
      );
    }
  }

  function createConfirm(messages: string[]) {
    setConfirmMessages(messages);
    setConfirmMovesToSend(eventManager.gameMoveList.filter((m) => !m.forceEvaluate)); // forceEvaluate was sent when creating confirm dialog
  }

  function processDialogConfirmed() {
    if (confirmPopupMessage) {
      confirmPopupResolve?.();
      return;
    }
    setConfirmMessages([]);
    eventManager.gameMoveList = confirmMovesToSend;
    if (eventManager.sendMoves) {
      eventManager.sendMoves();
    }
    setCheatMode(false);
  }

  function processAlertCheated() {
    eventManager.gameMoveList = confirmMovesToSend;
    if (eventManager.sendMoves) {
      eventManager.sendMoves();
    }
    setCheatMode(false);
  }

  function processDialogRejected() {
    setConfirmMessages([]);
  }

  function getStartPositionCopy() {
    return Object.assign(
      _.cloneDeep(stateOfGameManager),
      JSON.parse(JSON.stringify(startPositionRef.current)),
    ) as GameSituationBackend;
  }

  const moveToEvent = useCallback(
    (eventNumber: number) => {
      if (tableStatus !== "ended") {
        return;
      }

      if (eventNumber < 0) {
        eventNumber = 0;
      }

      if (eventNumber > allEventsRef.current.length) {
        eventNumber = allEventsRef.current.length;
      }

      if (eventNumber < currEventNumber.current) {
        tableStateRef.current = workingTableStateRef.current = getStartPositionCopy();

        if (startPositionRef.current) {
          tableStateRef.current = workingTableStateRef.current = startPositionRef.current;
          evalEvents(allEventsRef.current, 0, eventNumber);
        }
      } else {
        evalEvents(allEventsRef.current, currEventNumber.current, eventNumber);
      }
      currEventNumber.current = eventNumber;
    },
    [tableStatus],
  );

  function evalEvents(
    events: PlayerEvent[],
    start: number = 0,
    end: number = Infinity,
    stateDraft?: GameSituationBackend,
  ) {
    let localLogMessages: LogMessageData[] = [];
    if (!stateDraft) {
      stateDraft = Object.assign(
        _.cloneDeep(stateOfGameManager),
        JSON.parse(JSON.stringify(tableStateRef.current || startPositionRef.current)),
      );
    }
    if (stateDraft) {
      // really not sure why typescript is complaining without this check
      addChildData(stateDraft);
    }
    let logTurn = events.slice(0, start).filter((turn) => !turn.__undone && !(turn.type === "undo")).length;
    let evaledTurns = new Set();
    for (let e = start; e < Math.min(end, events.length); ++e) {
      let event = events[e];
      if (event.type === "undo") {
        let turnMessage = logMessage({
          logMessage: "(undo by {sourcePlayer})",
          currTurn: logTurn,
          systemTurn: true,
          timestamp: event.timestamp,
          sourcePlayer: event.data?.sourcePlayer,
          logStyle: {
            fontSize: "8px",
            outline: "none",
            margin: "-5px 0",
          },
        } as SystemLogMessage);
        localLogMessages.push(turnMessage);
      }

      if (event.type === "game-move" && !event.__undone) {
        let logTurn = event.data?.moveId;
        let turnMessage = logMessage({
          logMessage: "Turn {currTurn}" + (event.__undone ? " (undone)" : ""),
          currTurn: logTurn,
          systemTurn: true,
          timestamp: event.timestamp,
          logStyle: {
            fontSize: "8px",
            outline: "none",
            margin: "-5px 0",
          },
        } as SystemLogMessage);
        if (event.turnUID && evaledTurns.has(event.turnUID)) {
          continue;
        }
        evaledTurns.add(event.turnUID);
        localLogMessages.push(turnMessage);
        if (event.data?.moveList) {
          for (let move of event.data.moveList) {
            if (move.gameMoveUID && stateDraft?.evaledMoves) {
              if (stateDraft.evaledMoves[move.gameMoveUID]) {
                continue;
              }
              stateDraft.evaledMoves[move.gameMoveUID] = true;
            }
            move.username = event.username;
            move.timestamp = event.timestamp;
            evalGameMove(JSON.parse(JSON.stringify(move)), stateDraft, localLogMessages);
          }
        }
      }
    }

    if (stateDraft?.lastActivePlayer) {
      activatePlayer(stateDraft.lastActivePlayer);
    }
    tableStateRef.current = stateDraft;
    workingTableStateRef.current = stateDraft;
    if (start === 0) {
      setLogMessages(localLogMessages);
    } else {
      setLogMessages((messages) => messages.concat(localLogMessages));
    }
  }

  function generateFullLog(events: PlayerEvent[]) {
    let generatedLogMessages: LogMessageData[] = [];

    let evaledTurns = new Set();
    for (let e = 0; e < events.length; ++e) {
      let event = events[e];

      if (event.type === "game-move" && !event.__undone) {
        let logTurn = event.data?.moveId;
        let turnMessage = logMessage({
          logMessage: "Turn {currTurn}" + (event.__undone ? " (undone)" : ""),
          currTurn: logTurn,
          systemTurn: true,
          timestamp: event.timestamp,
          logStyle: {
            fontSize: "8px",
            outline: "none",
            margin: "-5px 0",
          },
        } as SystemLogMessage);
        if (event.turnUID && evaledTurns.has(event.turnUID)) {
          continue;
        }
        evaledTurns.add(event.turnUID);
        generatedLogMessages.push(turnMessage);
        if (event.data?.moveList) {
          for (let move of event.data.moveList) {
            move.username = event.username;
            move.timestamp = event.timestamp;
            generatedLogMessages.push(logMessage(JSON.parse(JSON.stringify(move))));
          }
        }
      }
    }

    setLogMessages(generatedLogMessages);
  }

  function activatePlayer(color: string) {
    if (playAsColor === "alwaysactive") {
      switchPlayer(color);
    }
  }

  function switchPlayer(color: string) {
    if (color !== "spectator" && color !== "alwaysactive" && color !== "everyone") {
      setSelectedColor(color);
    }
  }

  function changeCurrentPlayer(newColor: string) {
    setPlayAsColor(newColor);
    switchPlayer(newColor);
    localStorage.setItem("play-as-color-" + params.tableId, newColor);
    if (newColor === "alwaysactive") {
      switchPlayer(tableStateRef.current?.getActiveState().activePlayer ?? "");
    }
  }

  function getComponentInfo(componentData: GeneralComponentForGame): ComponentDataSource | undefined {
    if (!componentData) {
      return;
    }

    const cache = componentInfoCacheRef.current;
    const currentTableState = tableStateRef.current;

    // Invalidate cache when table state, component pool, or gameState id/version changes
    if (
      cache.tableStateVersion !== currentTableState ||
      cache.componentPoolVersion !== window.componentPool ||
      cache.adjustedCurrentState?.stateId !== currentTableState?.gameState.current?.stateId ||
      cache.adjustedCurrentState?.stateVersion !== currentTableState?.gameState.current?.stateVersion
    ) {
      cache.tableStateVersion = currentTableState;
      cache.componentPoolVersion = window.componentPool;
      cache.adjustedCurrentState = null;
      cache.results.clear();
    }

    // Use component instance for cache key to guarantee uniqueness across identical names with different options
    const cacheKey = componentData;
    if (cache.results.has(cacheKey)) {
      return cache.results.get(cacheKey);
    }

    let overdrive = { ...((currentTableState?.componentOverdrive ?? {})[componentData.name] ?? {}) };

    // Compute adjusted current state once per cache generation (same for all components)
    if (!cache.adjustedCurrentState) {
      let currState = currentTableState?.gameState.current;
      while (currState?.subState) {
        currState = currState.subState;
      }
      cache.adjustedCurrentState = adjustGameState(currState as GameState);
    }

    let stateOverdrive = (cache.adjustedCurrentState?.componentOverdrive ?? {})[componentData.name];
    if (stateOverdrive && overdrive) {
      overdrive = JSON.parse(JSON.stringify(overdrive));
      mergeDeep(overdrive, stateOverdrive);
    }
    if (!overdrive && stateOverdrive) {
      overdrive = stateOverdrive;
    }
    if (!componentData.name) {
      componentData.name = "__revealTemp";
    }
    let additionalOptions;
    if (componentData.name === "player-area") {
      let player = currentTableState?.players?.filter((p: PlayerInfo) => p.color === componentData.options.color)[0];
      additionalOptions = player?.customAttrs;
    }

    const result = processComponentInfo(componentData, overdrive, additionalOptions);
    cache.results.set(cacheKey, result);
    return result;
  }

  /** Clears the getComponentInfo cache. Call when the table state is mutated in-place. */
  function clearComponentInfoCache() {
    componentInfoCacheRef.current.results.clear();
    componentInfoCacheRef.current.adjustedCurrentState = null;
  }

  getComponentInfoRef.current = getComponentInfo;

  const targetScaleValue = useMemo(() => {
    let targetScale = 1;
    if (componentPool) {
      // depend on the state, not the window generic
      let currentGetComponentInfo = getComponentInfoRef.current;
      let rootInfo = currentGetComponentInfo(tableStateRef.current?.root as GameComponentForGame);
      let displayWidth = rootInfo?.displayWidth ?? rootInfo?.width ?? 1000;
      let baseScale = windowWidth / displayWidth; // prefer windowWidth over window.innerWidth
      if (window.mobileCheck && window.mobileCheck()) {
        baseScale = 0.3;
      }
      targetScale = (scaleAdjust / 100) * baseScale;
    }
    return targetScale;
  }, [scaleAdjust, windowWidth, componentPool]);

  const getTargetScale = useCallback(() => targetScaleValue, [targetScaleValue]);

  function getAnimationData(
    movingComponent: GeneralComponentForGame,
    destinationComponent: GameComponent,
    positionName: string,
    positionIndex: string | number,
    oldElement?: HTMLElement | null,
  ): GameComponentAnimationData | undefined {
    if (!oldElement) {
      oldElement = document.getElementById("wrap-" + movingComponent.id);
    }
    if (destinationComponent.id.includes("TempId")) {
      return undefined;
    }
    let oldElementBound = oldElement?.getBoundingClientRect();
    let oldX = 0,
      oldY = 0,
      oldWidth = 0;
    if (oldElementBound) {
      oldX = (oldElementBound.left + oldElementBound.right) / 2;
      oldY = (oldElementBound.top + oldElementBound.bottom) / 2;
      oldWidth = oldElementBound.width;
    }
    let newX = 0,
      newY = 0;
    let newPosition = document.getElementById(
      "position-" + destinationComponent.id + "-" + positionName + "-" + positionIndex,
    );
    let childrenAmt = 1;
    let childrenArr: GeneralComponentForGame[] = [];
    let children =
      destinationComponent.children &&
      destinationComponent.children[positionName] &&
      destinationComponent.children[positionName][positionIndex];
    if (Array.isArray(children)) {
      childrenAmt = children.length + 1;
      childrenArr = children;
    } else {
      childrenArr = [children];
    }
    let targetPosition = getPcCoords(
      destinationComponent,
      positionName,
      positionIndex,
      childrenAmt - 1,
      childrenArr,
      movingComponent,
      getComponentInfo(destinationComponent),
    );
    let [dx, dy] = [0, 0];
    if (newPosition && targetPosition) {
      [dx, dy] = [
        parseInt(newPosition.parentElement?.style.left ?? "0") - targetPosition[0],
        parseInt(newPosition.parentElement?.style.top ?? "0") - targetPosition[1],
      ];
    }
    let newPositionBound = newPosition?.getBoundingClientRect();
    if (newPositionBound) {
      newX = (newPositionBound.left + newPositionBound.right) / 2;
      newY = (newPositionBound.top + newPositionBound.bottom) / 2;
    }
    let scaleRatio = 1; //oldScale / newScale;
    let offsetX = ((oldX - newX) * scaleRatio) / getTargetScale();
    let offsetY = ((oldY - newY) * scaleRatio) / getTargetScale();
    return { offsetX, offsetY, oldWidth, scaleRatio, dx, dy, duration: 450, timestamp: Date.now() };
  }

  function evalGameMove(
    moveData: GameMoveForEval,
    gameSituation: GameSituationBackend | null = null,
    logMessages: LogMessageData[] | null = null,
    realTime = false,
  ) {
    let adjustFunction = (draft: GameSituationBackend | undefined): void => undefined;
    setUserSelectedComponent(null);
    if (eventManager.gameMoveList.length > 1000) {
      throw new Error("Too many moves added (possible infinite loop)");
    }
    if (gameSituation) {
      let animationData;
      let movingComponent = gameSituation.getGameComponentById(moveData.componentId) ?? moveData.revealedComponent;
      let destinationComponent = gameSituation.getGameComponentById(movingComponent?.parent);
      let anyMoveData = moveData as any;
      let oldElement;
      if (anyMoveData.destinationId) {
        destinationComponent = gameSituation.getGameComponentById(anyMoveData.destinationId);
      }
      if (anyMoveData.type === "component reveal") {
        oldElement = document.getElementById("wrap-" + anyMoveData.containerId);
      }
      if (destinationComponent && movingComponent && realTime) {
        animationData = getAnimationData(
          movingComponent as GeneralComponentForGame,
          destinationComponent,
          anyMoveData.positionName as string,
          anyMoveData.positionIndex as string | number,
          oldElement,
        );
      }
      adjustFunction = getAdjustFunction(
        moveData,
        gameSituation as GameSituationBackend,
        realTime,
        tableStatus === "midgame" && realTime,
        playAsColor,
        animationData,
      );
      switch (moveData.type) {
        case "custom":
          adjustFunction = (draft) => {
            if (!draft) {
              return;
            }
            evalCustomMoveRef.current(moveData, draft, manipulationMethods, logMessages, logMessage);
          };
          break;
        case "game end": {
          let tableStats = moveData.stats;

          setAllStats({
            playerStats: moveData.playerStats ?? {},
            tableStats: tableStats ?? {},
            objectStats: moveData.objectStats ?? {},
          });
          if (realTime) {
            setTableStatus("ended");
          }

          break;
        }
      }
    }
    let adjustInplace = !!gameSituation;
    if (moveData.eventRecipients) {
      moveData.eventRecipients.push("alwaysactive");
      if (!moveData.eventRecipients.includes(playAsColor ?? "")) {
        return; // skips the event if not in recipient list. TODO: do this on server side
      }
    }
    if (!moveData.logDisplayComponent && !moveData.preventLogDisplay) {
      moveData.logDisplayComponent =
        tableStateRef.current?.getGameComponentById(moveData.componentId) ??
        moveData.componentData ??
        (moveData.revealedComponent as GameComponentForGame);
    }
    if (moveData.type === "component reveal") {
      if (!moveData.revealedComponent) {
        moveData.revealedComponent = {
          name: "unknown",
          id: "0",
          children: {},
          childData: { childPos: "0", positionName: "" },
          options: {},
        };
      }
      let m = moveData as RevealComponentData;
      moveData.revealedComponent.parent = m.destinationId;
    }
    if (adjustInplace) {
      let depth = 0;
      if (moveData.type === "inc counter") {
        let gameMoveData = moveData as SetCounterData;
        if (realTime && gameMoveData.sourceComponent) {
          let sourceComponentId = "";
          if (typeof gameMoveData.sourceComponent == "string") {
            sourceComponentId = "wrap-" + gameMoveData.sourceComponent;
          } else {
            sourceComponentId = "wrap-" + gameMoveData.sourceComponent.id;
          }
          let sourceComponent = document.getElementById(sourceComponentId);
          let destinationComponent = document.getElementById("counter-" + gameMoveData.color + "-" + gameMoveData.name);
          if (sourceComponent && destinationComponent) {
            let startRect = sourceComponent.getBoundingClientRect();
            let endRect = destinationComponent.getBoundingClientRect();
            let situationMargin = document.querySelector(".game-situation")?.getBoundingClientRect();
            if (situationMargin) {
              let start = {
                x: (startRect.x + startRect.width / 2 - situationMargin.x) / getTargetScale(),
                y: (startRect.y + startRect.height / 2 - situationMargin.y) / getTargetScale(),
              };
              let end = {
                x: (endRect.x + endRect.width / 2 - situationMargin.x) / getTargetScale(),
                y: (endRect.y + endRect.height / 2 - situationMargin.y) / getTargetScale(),
              };

              if (+(gameMoveData.value ?? 0) < 0) {
                [start, end] = [end, start];
              }

              let iconURL = globalAssets?.[gameMoveData.name]?.url;

              let flyersCreated = 0;
              let flyerAmt = Math.abs(+(gameMoveData.value ?? 0));

              function createFlyer() {
                let flyer = document.createElement("div");
                flyer.classList.add("counter-flyer");
                document.querySelector(".flyer-wrap")?.appendChild(flyer);
                flyer.style.backgroundImage = `url(${iconURL})`;
                flyer.style.left = end.x + "px";
                flyer.style.top = end.y + "px";
                flyer
                  .animate(
                    [
                      {
                        transform:
                          "translate(-50%, -50%) " +
                          " translate(" +
                          (start.x - end.x) +
                          "px, " +
                          (start.y - end.y) +
                          "px) scale(0.5)",
                        opacity: 0,
                      },
                      {
                        transform:
                          "translate(-50%, -50%)" +
                          " translate(" +
                          (start.x - end.x) / 2 +
                          "px, " +
                          (start.y - end.y) / 2 +
                          "px) scale(1)",
                        opacity: 5,
                      },
                      {
                        transform: "translate(-50%, -50%) translate(0px, 0px) scale(0.5)",
                        opacity: 0,
                      },
                    ],
                    {
                      duration: 800 / evalSpeed,
                      easing: "ease",
                      fill: "forwards",
                    },
                  )
                  .finished.then(() => {
                    flyer.remove();
                  });

                ++flyersCreated;
                if (flyersCreated < flyerAmt) {
                  window.setTimeout(createFlyer, 150);
                }
              }
              if (flyerAmt > 0) {
                createFlyer();
                if (tableStatus === "midgame" && realTime) {
                  audio.gain.play();
                }
              }
            }
          }
        }
      }
      adjustFunction(gameSituation as GameSituationBackend);
      if (logMessages) {
        logMessages.push(logMessage(moveData, depth));
      }
    } else {
      adjustFunction(tableStateRef.current);
      if (logMessages) {
        setLogMessages((a) => a.concat(logMessage(moveData)));
      }
    }
  }

  function getIsClickable(
    componentData: GameComponentForGame,
    relativeState: GameSituationBackend = tableStateRef.current as GameSituationBackend,
  ) {
    let result: boolean | string = false;

    try {
      result = generalIsClickable && generalIsClickable(componentData, relativeState);

      // Cache adjustGameState result per relativeState identity to avoid
      // expensive JSON.parse(JSON.stringify(...)) on every component
      const cache = isClickableCacheRef.current;
      const currentActiveState = relativeState.getActiveState();
      if (cache.relativeStateVersion !== relativeState || cache.activeStateVersion !== currentActiveState) {
        cache.relativeStateVersion = relativeState;
        cache.activeStateVersion = currentActiveState;
        cache.adjustedState = adjustGameState(currentActiveState);
        cache.setupDone = false;
      }
      let state = cache.adjustedState!;
      let stateFunction = state.isClickable;
      if (stateFunction) {
        if (!cache.setupDone) {
          initialSetup(relativeState);
          cache.setupDone = true;
        }
        let stateResult = stateFunction(componentData, playAsColor);
        if (stateResult) {
          result = stateResult;
        }
      }
    } catch {
      // keep clickable on false
    }
    return result;
  }

  function fetchTable() {
    let inviteQuery = "";
    if (query.get("invitePwd")) {
      inviteQuery =
        "&invitePwd=" +
        query.get("invitePwd") +
        (query.get("playAsColor") ? "&playAsColor=" + query.get("playAsColor") : "");
    }
    fetch("/api/table-open/" + params.tableId + "?sessionId=" + sessionStorage.sessionId + inviteQuery, {
      method: "GET",
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then((data) => {
        console.log(data);
        console.log("setting up table", data);
        setTableStatus(data.status);
        setTableData(data);
        setDecks(data.deckData);
        setGlobalAssets(data.projectInfo.globalAssets);
        setConnectedPlayers(new Set(data.connectedPlayers));
        if (data.status === "open") {
          setChatMessages(data.chatMessages ?? []);
        } else {
          setupTable(data);
          let activeState = tableStateRef.current?.getActiveState();
          let playAsColor = localStorage.getItem("play-as-color-" + params.tableId);
          let competitiveForcedColor = data?.playerColors?.find(
            (c: PlayerColorInfo) => c.userInfo?.username === localStorage.getItem("user-name"),
          )?.color;
          if (competitiveForcedColor) {
            playAsColor = competitiveForcedColor;
          }
          let selectedColor =
            query.get("playAsColor") ??
            competitiveForcedColor ??
            playAsColor ??
            activeState?.activePlayer ??
            "everybody";
          if (!tableStateRef.current?.players.find((p: PlayerInfo) => p.color === selectedColor)) {
            selectedColor = tableStateRef.current?.getActiveState().activePlayer;
            if (!tableStateRef.current?.players.find((p: PlayerInfo) => p.color === selectedColor)) {
              selectedColor = tableStateRef.current?.players[0]?.color;
            }
          }
          setSelectedColor(selectedColor);
          if (tableStateRef.current && eventManager) {
            //evalAutoStates(tableStateRef.current, eventManager, activeState?.activePlayer)
            if (data.events?.length === 0) {
              eventManager.newEvent("delayed eval", {
                type: "table-first-visit",
                username: playAsColor ?? "",
                tableId: params.tableId ?? "",
              });
            }
            let stateInfo = adjustGameState(tableStateRef.current.getActiveState());
            if (stateInfo.delayedEval && tableStatus === "midgame") {
              eventManager.newEvent("delayed eval", {
                type: "delayed-eval",
                username: activeState?.activePlayer,
                timestamp: 0,
                tableId: params.tableId ?? "",
              });
            }
          }
        }
      })
      .catch((error) => {
        console.error(error);
        setTableJoinFailedDialogReason(error.message ?? error.statusText);
      });
  }

  function addParentIds(component: GameComponentForGame) {
    for (let parent of getAllComponentChildren(component)) {
      for (let child of getAllComponentDirectChildren(parent)) {
        child.parent = parent.id;
      }
    }
  }

  function setupTable(data: SetupTableData) {
    if (data.status === "notexist") {
      setTableStatus("notexist");
      return;
    }
    setIsEditorBound(data.isBound ?? false);
    addParentIds(data.start_position.root);
    if (data.start_position.players) {
      for (let p of data.start_position.players) {
        addParentIds(p.area);
      }
    }
    adjustComponentPool(data.componentPool, data.deckData);
    setComponentPool(data.componentPool);
    window.componentPool = data.componentPool;
    for (let deckName in data.deckData) {
      loadFonts(window, data.deckData[deckName]);
    }

    startPositionRef.current = Object.create(stateOfGameManager);
    Object.assign(
      startPositionRef.current ?? {},
      JSON.parse(JSON.stringify(data.start_position)) as GameSituationBackend,
    );

    setAllStats({ playerStats: data.playerStats, tableStats: data.stats, objectStats: data.objectStats });

    if (startPositionRef.current) {
      startPositionRef.current.deckData = data.deckData;
      startPositionRef.current.dataSheet = data.dataSheet;
      startPositionRef.current.componentOverdrive = {};
      startPositionRef.current.evaledMoves = {};
      for (let player of Object.values(startPositionRef.current.players ?? {})) {
        player.playTime = 0;
      }
    } else {
      console.error("Something went wrong, no start position found");
    }
    tableStateRef.current = workingTableStateRef.current = getStartPositionCopy();

    tableStateRef.current.setGameState(JSON.parse(JSON.stringify(data.start_position.gameState.current)) as GameState);
    addChildData(tableStateRef.current);
    console.time("evalEvents");
    evalEvents(JSON.parse(JSON.stringify(data.events)), 0, data.events.length, tableStateRef.current);
    console.timeEnd("evalEvents");
    setChatMessages([...(data.chatMessages || [])]);

    let activeState = tableStateRef.current?.getActiveState();

    allEventsRef.current = data.events;
    currEventNumber.current = data.events.length;

    setCanSelectColor(
      data.tableMode?.includes("sandbox") ||
        data.tableMode === "debug" ||
        data.tableMode == "demo-invite" ||
        data.status === "ended" ||
        localStorage.getItem("user-role") === "superadmin",
    );
    if (query.get("playAsColor")) {
      setCanSelectColor(false);
    }
    if (data.playerColors && data.tableMode !== "demo-invite") {
      let playingAs = "spectator";
      for (let p of data.playerColors) {
        if (p.userInfo?.username === localStorage.getItem("user-name")) {
          playingAs = p.color;
        }
      }
      if (data.tableMode.includes("sandbox") && playingAs === "spectator") {
        playingAs = "alwaysactive";
      }
      if (playingAs !== "spectator") {
        changeCurrentPlayer(playingAs);
      }

      let playerColors = data.playerColors;
      let players = data.start_position.players || [];
      for (let player of players) {
        let playerColor = playerColors.find((c: PlayerColorInfo) => c.color === player.color);
        if (!playerColor) {
          playerColors.push({ color: player.color });
        }
      }

      setPlayerColors(playerColors);
    }

    let decks = data.deckData;
    setDecks(decks);

    setTimeout(resizeCallback, 100);
    setTableLoaded(true);
    setTableStatus(data.status);
  }

  useEffect(() => {
    if (tableStatus === "midgame" && window.tableEventQueue && window.tableEventQueue.length > 0) {
      evalNextTableEvent();
    }
  }, [eventManager, tableStatus, window.tableEventQueue?.length]);

  function resizeCallback(e?: UIEvent) {
    let width = window.innerWidth;
    let rootInfo = getComponentInfo(tableStateRef.current?.root as GameComponentForGame);
    setWindowWidth(width); // just to force redraw of gamesituation that reads window.innerWidth as well
  }
  function convertGridCoordsToPcCoords(
    positionName: string,
    compData: GameComponentForGame,
    componentInfo: ComponentDataSource = {},
    targetCoords: { x: number; y: number },
  ) {
    let gridProps = componentInfo.positions?.[positionName]?.gridCoords;
    if (!gridProps) {
      return { x: 0, y: 0 };
    }
    let allCoords = Object.keys(compData.children[positionName] ?? {})
      .filter((key) => compData.children[positionName][key])
      .map((c) => c.split(":").map((n) => +n));
    if (allCoords.length === 0) {
      allCoords = [[0, 0]];
    }
    let minX = Math.min(...allCoords.map((c) => c[0]));
    let maxX = Math.max(...allCoords.map((c) => c[0]));
    let minY = Math.min(...allCoords.map((c) => c[1]));
    let maxY = Math.max(...allCoords.map((c) => c[1]));
    if (!gridProps.flexible) {
      minX = minY = 0;
      maxX = gridProps.maxCols - 1;
      maxY = gridProps.maxRows - 1;
    }
    let offset = { x: -(minX + maxX) / 2, y: -(minY + maxY) / 2 };
    let dx = gridProps.maxWidth / gridProps.maxCols;
    let dy = gridProps.maxHeight / gridProps.maxRows;
    return {
      x: (gridProps.center.x ?? 0) + dx * (offset.x + targetCoords.x),
      y: (gridProps.center.y ?? 0) + dy * (offset.y + targetCoords.y),
    };
  }
  const getPcCoords = useCallback(function getPcCoords(
    compData: GeneralComponentForGame,
    positionName: string,
    positionIndex: number | string,
    positionMultiI: number,
    allChildren?: GeneralComponentForGame[],
    childData?: GeneralComponentForGame,
    componentInfo: ComponentDataSource = {},
  ) {
    if (!componentInfo?.positions) {
      componentInfo.positions = {};
    }

    // getComponentInfoRef.current is already globally cached via componentInfoCacheRef
    const currentGetComponentInfo = getComponentInfoRef.current;

    function getGapSize(c: GeneralComponentForGame, left: boolean) {
      let childInfo = currentGetComponentInfo(c);
      if (!childInfo) {
        return 0.5;
      }
      return (
        (left ? childInfo.multiPositionGapLeft : childInfo.multiPositionGapRight) ?? childInfo.multiPositionGap ?? 0.5
      );
    }
    let coords = componentInfo.positions[positionName]?.coords || {};
    let [xDim, yDim] = [100, 100];
    if (componentInfo?.positions?.dims) {
      [xDim, yDim] = componentInfo.positions.dims;
    }
    let srcX = 0,
      srcY = 0;
    if (coords[positionIndex]) {
      srcX = coords[positionIndex].x;
      srcY = coords[positionIndex].y;
    }
    if (childData) {
      let childComponentInfo = currentGetComponentInfo(childData);
      if (childComponentInfo?.x) {
        srcX = childComponentInfo.x;
        srcY = childComponentInfo.y;
      }
    } else if (!componentInfo.positions[positionName] && !componentInfo.x && !componentInfo.y) {
      return [0, 0];
    }
    if (componentInfo.positions[positionName]?.gridCoords) {
      let [gridX, gridY] = ("" + positionIndex).split(":").map((n) => +n);
      ({ x: srcX, y: srcY } = convertGridCoordsToPcCoords(
        positionName,
        compData as GameComponentForGame,
        componentInfo,
        { x: gridX, y: gridY },
      ));
    }
    if (childData?.options?.position?.left) {
      srcX = childData.options.position.left;
      srcY = childData.options.position.top;
    }
    let x = (srcX / xDim) * 100;
    let y = (srcY / yDim) * 100;
    let positionInfo: ComponentDataSource | undefined = componentInfo.positions[positionName];
    if (positionInfo?.multi && positionInfo?.multiPile && positionMultiI !== undefined) {
      // Spiral arrangement for multiPile (hex grid, filled from center)
      const horizGap = positionInfo.horizGap ?? 6;
      const vertGap = positionInfo.vertGap ?? (Math.sqrt(3) * horizGap) / 2;
      const childCount = allChildren?.length ?? 0;
      const xd = PILE_XD_TABLE[childCount] ?? 0;
      const xy = PILE_XY_TABLE[childCount] ?? 0;

      function hexSpiral(n: number): [number, number] {
        if (n === 0) return [xd * horizGap, xy * vertGap];
        let layer = 1;
        let count = 1;
        while (count + 6 * layer <= n) {
          count += 6 * layer;
          layer++;
        }
        let pos = n - count;
        let side = Math.floor(pos / layer);
        let offset = pos % layer;
        if (layer > 1) {
          offset -= 1; // some weird off-by-one, don't really understand why
        }
        let hx = layer * HEX_DIRECTIONS[side % 6][0] + offset * HEX_DIRECTIONS[(side + 1) % 6][0] + xd;
        let hy = layer * HEX_DIRECTIONS[side % 6][1] + offset * HEX_DIRECTIONS[(side + 1) % 6][1] + xy;
        return [hx * horizGap, hy * vertGap];
      }
      const [pileOffsetX, pileOffsetY] = hexSpiral(positionMultiI);
      return [x + pileOffsetX, y + pileOffsetY];
    }
    if (positionMultiI !== undefined && positionInfo?.multi && (allChildren?.length ?? 0) > 0) {
      let coords = positionInfo.coords ?? {};
      let multiOverload = coords[positionIndex]?.multiOverload ?? [];

      if (multiOverload && multiOverload[positionMultiI]) {
        let overload = multiOverload[positionMultiI];
        return [(overload.x / xDim) * 100, (overload.y / yDim) * 100];
      }

      let totalChildrenLength = 1;
      if (allChildren) {
        totalChildrenLength = allChildren.slice(multiOverload.length).reduce((s, c, i) => {
          return s + ((i > 0 ? getGapSize(c, true) : 0.5) + (i < allChildren.length - 1 ? getGapSize(c, false) : 0.5));
        }, 0);
      }
      let gapSize = coords[positionIndex]?.gapSize ?? positionInfo?.gapSize ?? 60;
      if (positionInfo?.maxSpread && gapSize * (totalChildrenLength - 1) > positionInfo?.maxSpread) {
        gapSize = positionInfo?.maxSpread / (totalChildrenLength - 1);
      }
      let dx = (gapSize / componentInfo.width) * 100;
      let dy = 0;
      let align = positionInfo?.multiAlign ?? "center";

      let multiDir = componentInfo.positions[positionName]?.multiDirection;
      if (!componentInfo.positions) {
        return [0, 0];
      }
      if (!componentInfo.positions[positionName]?.coords) {
        return [0, 0];
      }
      if (componentInfo.positions[positionName]?.coords[positionIndex]) {
        let positionMultiDir = componentInfo.positions[positionName].coords[positionIndex].multiDirection;
        if (positionMultiDir) {
          multiDir = positionMultiDir;
        }
      }
      if (multiDir) {
        dx = ((gapSize * Math.cos(multiDir)) / componentInfo.width) * 100;
        dy = ((gapSize * Math.sin(multiDir)) / componentInfo.width) * 100;
      }
      let sy = y - dy * 0.5 * (totalChildrenLength - 1);
      let sx = x - dx * 0.5 * (totalChildrenLength - 1);
      if (!allChildren) {
        return [0, 0];
      }
      let realMultiplier = 0;
      for (let c = multiOverload.length; c < positionMultiI; ++c) {
        realMultiplier += getGapSize(allChildren[c], false) + getGapSize(allChildren[c + 1], true);
      }
      if (align === "left") {
        sx = x;
        sy = y;
        realMultiplier += getGapSize(allChildren[0], true);
      }
      return [sx + dx * realMultiplier, sy + dy * realMultiplier];
    }
    return [x, y];
  }, []); // stable: reads getComponentInfoRef.current at call time

  useEffect(() => {
    let importPromises = [
      getMoveManagerImport(params.projectSlug as string).then((module) => {
        evalGameEventRef.current = module.evalGameEvent;
        evalClickRef.current = module.evalClick;
        if (module && "tableFirstVisit" in module && module.tableFirstVisit) {
          tableFirstVisitRef.current = module.tableFirstVisit;
        }
      }),
      getCustomMovesImport(params.projectSlug as string).then((module) => {
        evalCustomMoveRef.current = module.default;
      }),
      getHighlightImport(params.projectSlug as string).then((module) => {
        if (module?.isClickable) {
          setGeneralIsClickable(() => module?.isClickable);
        } else {
          setGeneralIsClickable(() => module?.isClickable ?? (() => () => false));
        }
        isPosVisibleRef.current = module?.isPosVisible || (() => false);
      }),
      getCustomTooltipImport(params.projectSlug as string)
        .then((module) => {
          setCustomTooltip(() => module?.customTooltip || (() => () => false));
        })
        .catch(() => true),
      getStatesImport(params.projectSlug as string).then((module) => {
        statesInfo.current = module.states;
        addStateButtonsRef.current = (module as any).addStateButtons; // sorry...
      }),
    ];
    Promise.all(importPromises).then(() => {
      fetchTable();
    });
    function cleanup() {
      fetch("/api/table-close/" + params.tableId + "?sessionId=" + sessionStorage.sessionId, { method: "GET" });
    }
    window.addEventListener("beforeunload", cleanup);
    return () => {
      socket.removeAllListeners("table-event");
      cleanup();
    };
    //(omitting fetchTable, we want to run this only dependent on these variables)
    // eslint-disable-next-line
  }, [params.projectSlug, params.tableId, socket]);

  async function fakeReloadComponents() {
    console.log("reloading components", params);
    let newComponentsModule = await getComponentsImport(params.projectSlug as string);
    let newComponents = newComponentsModule.default;
    adjustComponentPool(newComponents, decks);
    window.componentPool = newComponents;
    setComponentPool(newComponents);
  }

  function skipAnimation() {
    /* sorry for using global here. But I didn't find other way to pass this to an async
    move evaluation, and this just works */
    window.skipAnimation = true;
  }

  useEffect(() => {
    const loadImages = async () => {
      if (!params.projectSlug) return;

      // Load images asynchronously
      const images = await importAll(params.projectSlug);
      imagesRef.current = images;

      console.log("Images loaded", images);
    };

    // Add the resize event listener
    window.addEventListener("resize", resizeCallback);

    // Call the async function to load images
    loadImages();

    console.log("joining table", params.tableId);
    window.tableEventQueue = [];
    window.evaluatedMoves = new Set();

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", resizeCallback);
    };
  }, [params.projectSlug, params.tableId, socket]);

  useEffect(() => {
    document.title = (tableData?.projectInfo?.project_name ?? "loading ") + " table - CGO";
  }, [tableData]);
  useEffect(() => {
    if (!editMode && statusBarStatus === "waitUserInput" && startPositionRef.current) {
      let lastSocketId = allEventsRef.current?.slice(-1)[0]?.data?.socketId; // delayedEval only triggers by the same socket that caused it
      if (!firstLoadDelayedChecked) {
        lastSocketId = undefined; // first opening of table ignores socketId to ensure it runs in stale game states
        setFirstLoadDelayedChecked(true);
      }
      checkDelayedEval(lastSocketId, undefined, Date.now());
    }
  }, [
    params.tableId,
    startPositionRef.current,
    editMode,
    statusBarStatus,
    firstLoadDelayedChecked,
    turnToConfirm.current,
    confirmedTurnCount,
  ]);

  const tableEventCallback = useCallback(
    (msg: GameMoveData) => {
      if (msg.tableId !== params.tableId && msg.type !== "user-leave") {
        console.warn("Received table event for different table:", msg.tableId, "expected:", params.tableId);
        return;
      }
      window.tableEventQueue.push(msg);
      if (msg.type === "game-move" && (msg as PlayerEvent)?.data?.moveId == turnToConfirm.current) {
        turnToConfirm.current = -1;
        // a move already evaluated locally is dropped below without a state change, so without this
        // render the delayed-eval effect would wait for an unrelated one (a hover, or the ~25 s socket heartbeat)
        setConfirmedTurnCount((count) => count + 1);
      }
      if (window.tableEventQueue.length > 1) {
        return; // will be evaluated at end of evalNextTableEvent
      }
      if (tableStatus !== "midgame" && msg.type === "game-move") {
        console.log("table not in midgame, skipping game move eval");
        // will be evaled after tableStatus changes to midgame in useEffect
        return;
      }
      evalNextTableEvent();
    },
    [evalNextTableEvent, tableStatus],
  );

  const tableEventRejectCallback = useCallback((msg: any) => {
    if (msg.reason === "outOfSync") {
      setOutOfSync(true);
    }
    if (msg.reason === "tableFirstVisitRace") {
      turnToConfirm.current = -1;
    }
  }, []);

  const tableRestartedHandler = useCallback(() => {
    console.log("table restarted, reloading components");
    window.location.reload();
  }, []);

  const pingHandler = useCallback(
    (data: PlayerEvent) => {
      console.log("pinged by", data);
      audio.chatPop.play();
      let pingElement = document.createElement("div");
      pingElement.className = "ping ping-" + data.sourcePlayer;
      pingElement.style.left = data.data?.x + "px";
      pingElement.style.top = data.data?.y + "px";
      pingElement.style.background = data.sourcePlayer ?? "red";
      document.querySelector(".game-situation")?.appendChild(pingElement);
      setTimeout(function () {
        pingElement.remove();
      }, 10000);
    },
    [audio.chatPop],
  );

  const tableEventCallbackRef = useRef(tableEventCallback);
  const tableEventRejectCallbackRef = useRef(tableEventRejectCallback);
  const tableRestartedHandlerRef = useRef(tableRestartedHandler);
  const pingHandlerRef = useRef(pingHandler);

  useEffect(() => {
    tableEventCallbackRef.current = tableEventCallback;
  }, [tableEventCallback]);

  useEffect(() => {
    tableEventRejectCallbackRef.current = tableEventRejectCallback;
  }, [tableEventRejectCallback]);

  useEffect(() => {
    tableRestartedHandlerRef.current = tableRestartedHandler;
  }, [tableRestartedHandler]);

  useEffect(() => {
    pingHandlerRef.current = pingHandler;
  }, [pingHandler]);

  useEffect(() => {
    console.log("setting socket.on");
    const handleTableEvent = (msg: GameMoveData) => tableEventCallbackRef.current(msg);
    const handleTableRestarted = () => tableRestartedHandlerRef.current();
    const handleTableEventReject = (msg: any) => tableEventRejectCallbackRef.current(msg);
    const handlePing = (data: PlayerEvent) => pingHandlerRef.current(data);

    socket.on("table-event", handleTableEvent);
    socket.on("table-restarted", handleTableRestarted);
    socket.on("table-event-reject", handleTableEventReject);
    socket.on("ping", handlePing);

    return () => {
      socket.off("ping", handlePing);
      socket.off("table-event", handleTableEvent);
      socket.off("table-event-reject", handleTableEventReject);
      socket.off("table-restarted", handleTableRestarted);
    };
  }, [socket]);
  const checkDelayedEval = useCallback(
    (originSocketId: string | undefined, originUsername: string | undefined, originTimestamp: number) => {
      if (tableStateRef.current?.getActiveState()) {
        let finalState = adjustGameState(tableStateRef.current?.getActiveState());
        if (
          typeof finalState.delayedEval === "function" &&
          (originSocketId === undefined || originSocketId === socket.id) &&
          tableStatus === "midgame" &&
          turnToConfirm.current === -1
        ) {
          // TODO
          eventManager.newEvent("delayed eval", {
            type: "delayed-eval",
            username: originUsername,
            timestamp: originTimestamp,
            tableId: params.tableId ?? "",
          });
        }
      }
    },
    [
      eventManager,
      tableStateRef.current,
      allEventsRef.current,
      turnToConfirm,
      tableStatus,
      socket.id,
      tableStateRef.current?.getActiveState().name,
      tableStateRef.current?.getActiveState().activePlayer,
    ],
  );

  // Dev-only handle for browser automation (window.__cgoTable, e.g. used by KCD's window.__kcd): read-only
  // table status for "is it safe to click" checks, the log, and the player selector's colour switch.
  const devTableHandleRef = useRef({
    statusBarStatus,
    tableStatus,
    tableLoaded,
    outOfSync,
    editMode,
    dialogOpen: false,
    playAsColor,
    viewingAs: undefined as string | undefined,
    makeTurnsAs,
    logMessages,
    changeCurrentPlayer,
  });
  devTableHandleRef.current = {
    statusBarStatus,
    tableStatus,
    tableLoaded,
    outOfSync,
    editMode,
    dialogOpen: confirmMessages.length > 0 || !!confirmPopupMessage,
    playAsColor,
    viewingAs: getViewingAsColor(),
    makeTurnsAs,
    logMessages,
    changeCurrentPlayer,
  };
  useEffect(() => {
    if (!(import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
      return;
    }
    window.__cgoTable = {
      status: () => {
        const { logMessages, changeCurrentPlayer, ...status } = devTableHandleRef.current;
        return {
          ...status,
          queuedEvents: window.tableEventQueue?.length ?? 0,
          awaitingServer: turnToConfirm.current !== -1,
          eventCount: allEventsRef.current?.length ?? 0,
        };
      },
      logMessages: () => devTableHandleRef.current.logMessages,
      changeCurrentPlayer: (color: string) => devTableHandleRef.current.changeCurrentPlayer(color),
    };
    return () => {
      delete window.__cgoTable;
    };
  }, []);

  return useMemo(() => {
    return {
      allEventsRef,
      tableStatus,
      currEventNumber: currEventNumber.current,
      moveToEvent,
      eventManager,
      isClickable: getIsClickable,
      evalClick: evalClickRef.current,
      customTooltip,
      tableStateRef,
      workingTableStateRef,
      getPcCoords,
      getComponentInfo,
      adjustGameState,
      playerColors,
      tableData,
      selectedColor,
      playAsColor,
      makeTurnsAs,
      decks,
      switchPlayer,
      evalAutoStates,
      reloadComponents: fakeReloadComponents,
      setEditMode,
      getImage,
      isPosVisible: isPosVisibleRef.current,
      getTargetScale,
      editMode,
      windowWidth,
      clipLog: false, //not used anywhere, remove later
      skipAnimation,
      cheatMode,
      defaultSelected,
      statusBarStatus,
      outOfSync,
      tableLoaded,
      changeCurrentPlayer,
      dialogMessages: confirmPopupMessage ? [confirmPopupMessage] : confirmMessages,
      dialogType: confirmPopupMessage ? "confirmPopup" : "confirmTurn",
      processDialogConfirmed,
      processDialogRejected,
      processAlertCheated,
      setCheatMode,
      logMessages,
      fetchTable,
      selectedComponent,
      setUserSelectedComponent,
      connectedPlayers,
      globalAssets,
      tableId: params.tableId ?? "",
    };
  }, [
    windowWidth,
    tableStateRef.current,
    editMode,
    playAsColor,
    selectedComponent,
    statusBarStatus,
    outOfSync,
    selectedColor,
    confirmMessages,
    workingTableStateRef.current,
    scaleAdjust,
    cheatMode,
    logMessages,
    tableLoaded,
    componentPool,
    currEventNumber,
    turnToConfirm,
    connectedPlayers,
    decks,
    confirmPopupMessage,
  ]);
}
